/**
 * boardMeta.ts — the Work Board's shared vocabulary: agent identity, work-type
 * glyphs, and small formatting helpers used by the card faces, filter pills,
 * calendar pills and the card drawer.
 */
import type { AgentId, Card, CardPriority, CardWorkType, Message } from '@shared/types';

export const AGENT_ORDER: AgentId[] = ['emma', 'milo', 'leo', 'quinn', 'iris', 'nora', 'grace'];

export const AGENT_LABELS: Record<AgentId, string> = {
  emma: 'Emma',
  milo: 'Milo',
  leo: 'Leo',
  quinn: 'Quinn',
  iris: 'Iris',
  nora: 'Nora',
  grace: 'Grace',
};

/** Orb identity colours — mirrors orbs.css so dots match each agent's orb. */
export const AGENT_COLORS: Record<AgentId, string> = {
  emma: '#5BD1F5',
  milo: '#4D7CFF',
  leo: '#46E0A3',
  quinn: '#8FA3B0',
  iris: '#9D8CFF',
  nora: '#FF6B5E',
  grace: '#FFC24D',
};

export const WORK_TYPE_META: Record<CardWorkType, { glyph: string; label: string }> = {
  campaign: { glyph: '✉', label: 'Email' },
  flow: { glyph: '⟳', label: 'Flow' },
  cro_test: { glyph: 'Δ', label: 'CRO test' },
  page: { glyph: '▤', label: 'Page' },
  creative: { glyph: '✦', label: 'Creative' },
  other: { glyph: '◈', label: 'Work' },
};

/**
 * Priority chips (drawer field row + card face). Chevron glyphs + a CSS class
 * per level — urgent burns coral, high amber, medium ice, low stays quiet.
 */
export const PRIORITY_META: Record<CardPriority, { label: string; glyph: string; cls: string }> = {
  low: { label: 'Low', glyph: '⌄', cls: 'prio-low' },
  medium: { label: 'Medium', glyph: '=', cls: 'prio-medium' },
  high: { label: 'High', glyph: '⌃', cls: 'prio-high' },
  urgent: { label: 'Urgent', glyph: '⌃⌃', cls: 'prio-urgent' },
};

export const PRIORITY_ORDER: CardPriority[] = ['urgent', 'high', 'medium', 'low'];

/** Every image referenced by ![alt](src) in a markdown body (R2 keys or https URLs). */
export function markdownImages(md: string | null | undefined): string[] {
  if (!md) return [];
  const out: string[] = [];
  const re = /!\[[^\]\n]*\]\(([^)\s]+)\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(md)) !== null) out.push(m[1]);
  return out;
}

/**
 * Everything worth showing in the Attachments tab: the preview asset, images
 * embedded in the description, and images in the receipts — de-duped, in that
 * order.
 */
export function cardAttachments(card: Card): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (k: string | null) => {
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  };
  push(previewAsset(card));
  for (const k of markdownImages(card.description_md)) push(k);
  for (const k of markdownImages(card.result_md)) push(k);
  return out;
}

/** '2026-07-14' → 'Jul 14' (local, calendar semantics). */
export function fmtDue(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Today as local 'YYYY-MM-DD' (due dates are calendar days, not instants). */
export function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The card's rendered preview asset key, if it has one. */
export function previewAsset(card: Card): string | null {
  const p = card.refs?.preview_asset;
  return typeof p === 'string' && p ? p : null;
}

/** First non-empty line of the receipts markdown, de-markdowned for a card face. */
export function receiptsLine(card: Card): string | null {
  const md = card.result_md;
  if (!md) return null;
  const line = md.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
  const plain = line.replace(/[#*_`>]/g, '').trim();
  return plain || null;
}

export function isImageAsset(key: string): boolean {
  return /\.(png|jpe?g|gif|webp|avif)$/i.test(key);
}

export function titleCase(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Human name for a thread entry's author ('You' for the signed-in founder). */
export function messageAuthor(m: Message, meEmail: string | null): string {
  if (m.author_type === 'founder') {
    if (meEmail && m.author_id === meEmail) return 'You';
    return titleCase(m.author_id.split('@')[0] ?? m.author_id);
  }
  if (m.author_type === 'agent') {
    return AGENT_LABELS[m.author_id as AgentId] ?? titleCase(m.author_id);
  }
  return 'HQ';
}

/** "'agent:milo'" → "Milo", "'founder:Zarg'" → "Zarg". */
export function createdByLabel(createdBy: string): string {
  const idx = createdBy.indexOf(':');
  if (idx === -1) return createdBy;
  const kind = createdBy.slice(0, idx);
  const name = createdBy.slice(idx + 1);
  if (kind === 'agent' && name) return AGENT_LABELS[name as AgentId] ?? titleCase(name);
  if (kind === 'founder' && name) return name;
  return createdBy;
}

/** Relative timestamp for thread entries — 'just now', '4m ago', 'Jul 8 · 9:12 AM'. */
export function fmtWhen(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = new Date(t);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}
